import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { clienteListItemSchema, imovelResumoSchema } from '@/modules/clientes/schemas';
import {
  grupoEconomicoSchema,
  gruposEconomicosListSchema,
  type GrupoEconomicoCreateInput,
  type GrupoEconomicoUpdateInput,
} from './schemas';

const keys = {
  list: () => ['grupos-economicos', 'list'] as const,
  detail: (id: number | string) => ['grupos-economicos', 'detail', String(id)] as const,
  clientes: (id: number | string) => ['grupos-economicos', 'clientes', String(id)] as const,
  imoveis: (id: number | string) => ['grupos-economicos', 'imoveis', String(id)] as const,
};

const okSchema = z.object({ ok: z.boolean().optional() });

export function useGruposEconomicos() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: ({ signal }) =>
      api('/api/v2/grupos-economicos', { schema: gruposEconomicosListSchema, signal }),
    staleTime: 30_000,
  });
}

export function useGrupoEconomico(id: number | null) {
  return useQuery({
    queryKey: keys.detail(id ?? 0),
    queryFn: ({ signal }) =>
      api(`/api/v2/grupos-economicos/${id}`, { schema: grupoEconomicoSchema, signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

const grupoClientesSchema = z.object({
  grupo: z.object({ id: z.coerce.number(), nome: z.string() }),
  clientes: z.array(clienteListItemSchema),
});

export function useClientesDoGrupo(id: number | null) {
  return useQuery({
    queryKey: keys.clientes(id ?? 0),
    queryFn: ({ signal }) =>
      api(`/api/v2/grupos-economicos/${id}/clientes`, {
        schema: grupoClientesSchema,
        signal,
      }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

const grupoImoveisSchema = z.object({
  grupo: z.object({
    id: z.coerce.number(),
    nome: z.string(),
    cnpj: z.string().nullish(),
  }),
  imoveis: z.array(imovelResumoSchema),
});

export function useImoveisDoGrupo(id: number | null) {
  return useQuery({
    queryKey: keys.imoveis(id ?? 0),
    queryFn: ({ signal }) =>
      api(`/api/v2/grupos-economicos/${id}/imoveis`, {
        schema: grupoImoveisSchema,
        signal,
      }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateGrupoEconomico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GrupoEconomicoCreateInput) =>
      api('/api/v2/grupos-economicos', {
        method: 'POST',
        body: input,
        schema: grupoEconomicoSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useUpdateGrupoEconomico(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GrupoEconomicoUpdateInput) =>
      api(`/api/v2/grupos-economicos/${id}`, {
        method: 'PATCH',
        body: input,
        schema: grupoEconomicoSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
      if (id) void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDeleteGrupoEconomico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/v2/grupos-economicos/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}
